import React, { useEffect, useRef } from "react";
import Image from "next/image";
import * as LucideIcons from "lucide-react";
import MandalaWatermark from "./MandalaWatermark";

const defaultChips = [
  { iconName: "Trophy", label: "10,000+ Pujas Conducted" },
  { iconName: "Users", label: "2,000+ Verified Priests" },
  { iconName: "BookOpen", label: "600+ Pujas Available" },
  { iconName: "MapPin", label: "10+ Cities Covered" },
  { iconName: "Package", label: "All Samagri Included" },
  { iconName: "Globe", label: "10+ Languages" },
  { iconName: "IndianRupee", label: "Transparent Pricing" },
];


const defaultTestimonials = [
  {
    name: "places/ChIJuU73SL0RrjsRMNDzSXUagjk/reviews/Ci9DQUlRQUNvZENodHljRjlvT2tkak5GSkdNMTlWY20xTE1ESkZSMVpWU0hwT1gyYxAB",
    relativePublishTimeDescription: "a month ago",
    rating: 5,
    text: {
      text: "Namaste, We did pooja today at our Palakkad, Kerala home. It was done in a really devotional, calm and peaceful way. We are very happy with the pooja performed. Thanking team BookMyPooja for your timely support. Truly appreciated. 🙏🙏",
      languageCode: "en"
    },
    authorAttribution: {
      displayName: "Sreeja S",
      uri: "https://www.google.com/maps/contrib/103588426333180246855/reviews",
      photoUri: ""
    },
    googleMapsUri: "https://www.google.com/maps/reviews/data=!4m6!14m5!1m4!2m3!1sCi9DQUlRQUNvZENodHljRjlvT2tkak5GSkdNMTlWY20xTE1ESkZSMVpWU0hwT1gyYxAB!2m1!1s0x3bae11bd48f74eb9:0x39821a7549f3d030"
  },
  {
    rating: 5,
    text: {
      text: "Extremely professional service. The Pandit ji arrived on time and explained every mantra's significance during our Griha Pravesh. The samagri was of high quality and very well-packed.",
      languageCode: "en"
    },
    authorAttribution: {
      displayName: "Anjali Sharma",
      photoUri: "https://api.dicebear.com/7.x/avataaars/svg?seed=Anjali"
    },
    googleMapsUri: "https://www.google.com/maps/place/Book+My+Pooja/@12.9716,77.5946,17z/data=!4m8!3m7!1s0x3bae11bd48f74eb9:0x39821a7549f3d030!8m2!3d12.9841872!4d77.7288636!9m1!1b1!16s%2Fg%2F11f_6w0y7v?entry=ttu"
  },
  {
    rating: 5,
    text: {
      text: "We booked a Mahamrityunjay Jaap online for my parents. The streaming quality was excellent, and we felt as if we were sitting right there in the temple. Highly recommended for overseas Indians.",
      languageCode: "en"
    },
    authorAttribution: {
      displayName: "Rajesh Iyer",
      photoUri: ""
    },
    googleMapsUri: "https://www.google.com/maps/place/Book+My+Pooja/@12.9716,77.5946,17z/data=!4m8!3m7!1s0x3bae11bd48f74eb9:0x39821a7549f3d030!8m2!3d12.9841872!4d77.7288636!9m1!1b1!16s%2Fg%2F11f_6w0y7v?entry=ttu"
  },
  {
    rating: 5,
    text: {
      text: "The easiest way to book a puja. I was worried about finding a Marathi-speaking pandit in Delhi, but BookMyPooja made it happen seamlessly. The platform is truly a lifesaver.",
      languageCode: "en"
    },
    authorAttribution: {
      displayName: "Vikram Mehta",
      photoUri: ""
    },
    googleMapsUri: "https://www.google.com/maps/place/Book+My+Pooja/@12.9716,77.5946,17z/data=!4m8!3m7!1s0x3bae11bd48f74eb9:0x39821a7549f3d030!8m2!3d12.9841872!4d77.7288636!9m1!1b1!16s%2Fg%2F11f_6w0y7v?entry=ttu"
  }
];

const TrustSection = ({ testimonials, trustData }) => {
  const displayStats = trustData?.stats || defaultChips;
  const displayTestimonials = (testimonials && testimonials.length > 0) ? testimonials : defaultTestimonials;

  const scrollRef = useRef(null);

  useEffect(() => {
    const container = scrollRef.current;
    if (!container) return;

    let animationFrameId;
    let isPaused = false;
    let scrollPos = container.scrollLeft;
    let lastTime = performance.now();
    let isReversing = false;

    const scroll = (time) => {
      const delta = time ? (time - lastTime) : 16;
      lastTime = time || performance.now();
      
      if (!isPaused && container) {
        if (!isReversing) {
          scrollPos += (25 * delta) / 1000; // 25px per second, very slow smooth scroll
          if (scrollPos >= container.scrollWidth - container.clientWidth - 2) {
            isReversing = true;
          }
        } else {
          scrollPos -= (25 * delta) / 1000;
          if (scrollPos <= 2) {
            isReversing = false;
          }
        }
        
        container.scrollLeft = scrollPos;
      } else if (container) {
        // Track manual user scroll position
        scrollPos = container.scrollLeft;
        // Adjust direction if user manually scrolled to the ends
        if (scrollPos >= container.scrollWidth - container.clientWidth - 2) {
          isReversing = true;
        } else if (scrollPos <= 2) {
          isReversing = false;
        }
      }
      
      animationFrameId = requestAnimationFrame(scroll);
    };

    animationFrameId = requestAnimationFrame(scroll);

    const handlePause = () => { isPaused = true; };
    const handleResume = () => {
      isPaused = false;
      lastTime = performance.now();
      scrollPos = container.scrollLeft;
    };

    container.addEventListener("mouseenter", handlePause);
    container.addEventListener("mouseleave", handleResume);
    container.addEventListener("touchstart", handlePause, { passive: true });
    container.addEventListener("touchend", handleResume);

    return () => {
      cancelAnimationFrame(animationFrameId);
      if (container) {
        container.removeEventListener("mouseenter", handlePause);
        container.removeEventListener("mouseleave", handleResume);
        container.removeEventListener("touchstart", handlePause);
        container.removeEventListener("touchend", handleResume);
      }
    };
  }, []);

  return (
    <section id="testimonials" className="py-10 md:py-16 bg-secondary relative overflow-hidden">
      {/* Mandala watermark */}
      <MandalaWatermark className="absolute -left-32 top-10 w-[500px] h-[500px] text-primary opacity-[0.03] pointer-events-none" />

      <div className="container relative z-10">
        <h2 className="text-3xl md:text-4xl text-center mb-3">
          {trustData?.title?.split(trustData?.highlight)[0]} <span className="text-primary">{trustData?.highlight}</span> {trustData?.title?.split(trustData?.highlight)[1]}
        </h2>
        <p className="text-center text-muted-foreground text-lg mb-4 font-sans">{trustData?.subtitle}</p>
        <p className="text-center text-sm text-muted-foreground max-w-2xl mx-auto mb-6 font-sans">
          {trustData?.description}
        </p>

        {/* Marquee Trust Strip */}
        <div className="overflow-hidden mb-14 group">
          <div className="flex animate-marquee group-hover:[animation-play-state:paused] w-max">
            {[...displayStats, ...displayStats].map((chip, i) => {
              const Icon = LucideIcons[chip.iconName] || LucideIcons.CheckCircle;
              return (
                <div key={`${chip.label}-${i}`} className="flex items-center gap-1.5 whitespace-nowrap mx-4">
                  {i > 0 && <span className="text-border mr-2">•</span>}
                  <Icon className="h-4 w-4 text-primary flex-shrink-0" />
                  <span className="text-sm font-semibold text-foreground font-sans">{chip.label || chip.metric}</span>
                </div>
              );
            })}
          </div>
        </div>

        {/* Reviews */}
        <div className="mt-16">
          <h3 className="text-2xl md:text-3xl text-center mb-10">{trustData?.reviewsTitle || "What Families Say About Us"}</h3>
          <div 
            ref={scrollRef}
            className="flex overflow-x-auto gap-6 pb-6 [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]"
          >
            {displayTestimonials.map((review, i) => (
              <div
                key={`${review.name}-${i}`}
                className="min-w-[280px] w-[85vw] sm:w-[320px] md:w-[350px] flex-shrink-0 p-6 rounded-2xl bg-background border border-primary/10 shadow-sm hover:shadow-md transition-shadow duration-300 flex flex-col justify-between"
              >
                <div>
                  <div className="flex gap-0.5 mb-4">
                    {Array.from({ length: 5 }).map((_, starIdx) => (
                      <LucideIcons.Star
                        key={starIdx}
                        className={`h-4 w-4 ${starIdx < review.rating ? "fill-gold text-gold" : "text-border"}`}
                      />
                    ))}
                  </div>
                  <div className="mb-6">
                    <p className="text-[15px] text-muted-foreground leading-relaxed font-sans italic line-clamp-4 min-h-[5.5rem]">
                      {review.text?.text || review.text}
                    </p>
                    {review.googleMapsUri && (
                      <a 
                        href={review.googleMapsUri} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="text-[10px] text-primary hover:text-primary/80 font-bold uppercase tracking-wider mt-3 flex items-center gap-1 group w-fit transition-colors"
                      >
                        <span>{trustData?.viewGoogleReview || "View Review on Google"}</span>
                        <LucideIcons.ExternalLink className="h-2.5 w-2.5 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                      </a>
                    )}
                  </div>
                </div>

                <div className="flex items-center gap-3 pt-4 border-t border-primary/5">
                  <div className="relative w-9 h-9 rounded-full overflow-hidden bg-primary/5 flex-shrink-0 border border-primary/10">
                    <Image
                      src={review.authorAttribution?.photoUri || review.photo || `https://api.dicebear.com/7.x/avataaars/svg?seed=${review.authorAttribution?.displayName || review.name}`}
                      alt={`Review by ${review.authorAttribution?.displayName || "BookMyPooja Devotee"}`}
                      fill
                      className="object-cover"
                      sizes="36px"
                    />
                  </div>
                  <div className="overflow-hidden">
                    <p className="font-bold text-sm font-sans truncate">{review.authorAttribution?.displayName || review.name}</p>
                    {review.relativePublishTimeDescription && (
                      <p className="text-[10px] text-muted-foreground font-sans">{review.relativePublishTimeDescription}</p>
                    )}
                  </div>
                </div>
              </div>
            ))}
            
            <a
              href="https://www.google.com/maps/place/Book+My+Pooja/@12.9841872,77.7262887,17z/data=!4m8!3m7!1s0x3bae11bd48f74eb9:0x39821a7549f3d030!8m2!3d12.9841872!4d77.7288636!9m1!1b1!16s%2Fg%2F11f_6w0y7v?entry=ttu"
              target="_blank"
              rel="noopener noreferrer"
              className="min-w-[280px] w-[85vw] sm:w-[320px] md:w-[350px] flex-shrink-0 p-6 rounded-2xl bg-primary/5 border border-primary/10 hover:border-primary/30 transition-all duration-300 flex flex-col items-center justify-center text-center group"
            >
              <div className="w-16 h-16 rounded-full bg-background shadow-sm border border-primary/10 flex items-center justify-center mb-6 group-hover:scale-110 group-hover:shadow-md transition-all duration-300 relative overflow-hidden">
                <Image 
                  src="https://www.google.com/images/branding/googleg/1x/googleg_standard_color_128dp.png" 
                  alt="Google Reviews for BookMyPooja - 4.8 Stars" 
                  fill
                  className="object-contain p-4"
                  sizes="64px"
                />
              </div>
              <h3 className="text-xl md:text-2xl font-semibold mb-3">{trustData?.googleReviewsCta?.title}</h3>
              <p className="text-sm text-muted-foreground font-sans mb-6">{trustData?.googleReviewsCta?.subtitle}</p>
              
              <div className="flex gap-1 mb-6">
                {Array.from({ length: 5 }).map((_, i) => (
                  <LucideIcons.Star key={i} className="h-5 w-5 fill-gold text-gold" />
                ))}
              </div>

              <div className="flex items-center gap-2 text-primary font-bold uppercase tracking-wider text-xs">
                <span>{trustData?.googleReviewsCta?.buttonText}</span>
                <LucideIcons.ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
              </div>
            </a>
          </div>
        </div>
      </div>
    </section>
  );
};

export default TrustSection;
